/**
 * Hero: bronkeuze, autoplay-reparatie en parallax.
 *
 * De bron wordt met matchMedia gekozen en niet met een media-attribuut op
 * <source>. Browsers kijken daar hooguit een keer naar, bij de eerste load, en
 * Safari negeert het in de praktijk helemaal.
 */
const MOBILE = "(max-width: 767px)";

class FoleaHero extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("[data-hero-video]");
    this.hint = this.querySelector("[data-scroll-hint]");
    this.ticking = false;

    if (this.video) {
      this.pickSource();
      this.repairAutoplay();
    }

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    this.cleanupGestures?.();
    document.removeEventListener("visibilitychange", this.attempt);
  }

  /**
   * De mobiele variant is 1280px in plaats van 1920px en een fractie van de
   * bestandsgrootte, wat op mobiele data het verschil maakt tussen meteen
   * spelen en seconden wachten.
   */
  pickSource() {
    const desktop = this.dataset.videoDesktop;
    const mobile = this.dataset.videoMobile;
    const wanted = window.matchMedia(MOBILE).matches && mobile ? mobile : desktop;

    if (wanted && this.video.getAttribute("src") !== wanted) {
      this.video.setAttribute("src", wanted);
      this.video.load();
    }
  }

  /**
   * Autoplay wordt op mobiel regelmatig geweigerd: iOS in energiebesparings-
   * modus doet dat altijd, en de in-app browsers van WhatsApp en Instagram
   * vaak ook. Drie pogingen: direct, zodra het tabblad weer zichtbaar wordt,
   * en bij het eerste gebaar. Die laatste is de betrouwbaarste, want na een
   * gebruikersgebaar staat elke browser afspelen toe. Lukt geen ervan, dan
   * blijft de poster staan.
   */
  repairAutoplay() {
    const video = this.video;
    video.muted = true;
    video.defaultMuted = true;

    let done = false;
    const gestures = ["touchstart", "pointerdown", "keydown", "scroll"];

    this.cleanupGestures = () => {
      gestures.forEach((type) => window.removeEventListener(type, this.attempt));
    };

    this.attempt = () => {
      if (done) return;
      video.play().then(
        () => {
          done = true;
          this.cleanupGestures();
        },
        () => {},
      );
    };

    this.attempt();
    document.addEventListener("visibilitychange", this.attempt);
    gestures.forEach((type) =>
      window.addEventListener(type, this.attempt, { passive: true }),
    );
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      const height = this.offsetHeight || 1;
      const progress = Math.min(Math.max(-this.getBoundingClientRect().top / height, 0), 1);

      // De video schuift langzamer weg dan de pagina, wat diepte geeft.
      this.style.setProperty("--parallax", `${progress * height * 0.18}px`);

      if (this.hint) {
        this.hint.style.setProperty("--hint-opacity", String(Math.max(1 - progress / 0.15, 0)));
      }
      this.ticking = false;
    });
  }
}

if (!customElements.get("folea-hero")) {
  customElements.define("folea-hero", FoleaHero);
}
